import { useEffect, useState } from 'react';
import { IslamicEvent } from '../utils/islamicEvents';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { TimeAndPrayerDisplay } from './TimeAndPrayerDisplay';
import { PrayerTime } from '../types/prayer';

interface EventHadith {
  hadith_text: string;
  hadith_reference: string;
}

interface EventCountdownProps {
  event: IslamicEvent;
  daysUntil: number;
  prayers: PrayerTime[];
}

type SlideType = 'countdown' | 'hadith' | 'preparation';

export function EventCountdown({ event, daysUntil, prayers }: EventCountdownProps) {
  const [hadith, setHadith] = useState<EventHadith | null>(null);
  const [hoursUntil, setHoursUntil] = useState(0);
  const [minutesUntil, setMinutesUntil] = useState(0);
  const [currentSlide, setCurrentSlide] = useState<SlideType>('countdown');
  const { language } = useLanguage();

  useEffect(() => {
    async function fetchHadith() {
      const { data } = await supabase
        .from('islamic_event_hadiths')
        .select('hadith_text, hadith_reference')
        .eq('event_name', event.name)
        .maybeSingle();

      if (data) {
        setHadith(data);
      }
    }

    fetchHadith();
  }, [event.name]);

  useEffect(() => {
    function updateCountdown() {
      const now = new Date();
      const eventDate = new Date(event.date);
      const diff = eventDate.getTime() - now.getTime();

      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setHoursUntil(hours);
      setMinutesUntil(minutes);
    }

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [event.date]);

  useEffect(() => {
    const slides: SlideType[] = hadith
      ? ['countdown', 'hadith', 'preparation']
      : ['countdown', 'preparation'];

    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % slides.length;
      setCurrentSlide(slides[currentIndex]);
    }, 10000);

    return () => clearInterval(interval);
  }, [hadith]);

  const isLastDay = daysUntil === 0;

  const getEventEmoji = (name: string) => {
    if (name.includes('Ramadan')) return '🌙';
    if (name.includes('Hajj')) return '🕋';
    if (name.includes("Eid al-Fitr")) return '🌟';
    if (name.includes("Eid al-Adha")) return '🐑';
    if (name.includes('Muharram')) return '📅';
    if (name.includes('Ashura')) return '🕌';
    return '✨';
  };

  const getPreparationTips = (eventName: string): Array<{ en: string; ar: string; icon: string }> => {
    if (eventName.includes('Ramadan')) {
      return [
        { en: 'Start adjusting your sleep schedule', ar: 'ابدأ في تنظيم جدول نومك', icon: '😴' },
        { en: 'Plan your spiritual goals', ar: 'خطط لأهدافك الروحية', icon: '🎯' },
        { en: 'Prepare your Quran and dua books', ar: 'جهز مصحفك وكتب الأدعية', icon: '📚' },
        { en: 'Think about charity you will give', ar: 'فكر في الصدقات التي ستقدمها', icon: '💝' }
      ];
    } else if (eventName.includes('Hajj')) {
      return [
        { en: 'Review Hajj rituals', ar: 'راجع مناسك الحج', icon: '📖' },
        { en: 'Prepare your luggage', ar: 'جهز أمتعتك', icon: '🎒' },
        { en: 'Learn important duas', ar: 'تعلم الأدعية المهمة', icon: '🤲' },
        { en: 'Prepare physically and spiritually', ar: 'استعد بدنياً وروحياً', icon: '💪' }
      ];
    } else if (eventName.includes('Eid')) {
      return [
        { en: 'Plan family and friends visits', ar: 'خطط لزيارة العائلة والأصدقاء', icon: '👨‍👩‍👧‍👦' },
        { en: 'Prepare your new clothes', ar: 'جهز ملابسك الجديدة', icon: '👔' },
        { en: 'Think of gifts for children', ar: 'فكر في هدايا للأطفال', icon: '🎁' },
        { en: 'Plan Eid meal', ar: 'خطط لوجبة العيد', icon: '🍽️' }
      ];
    }
    return [
      { en: 'Increase your worship', ar: 'زد من عبادتك', icon: '🕌' },
      { en: 'Read about this event', ar: 'اقرأ عن هذا الحدث', icon: '📚' },
      { en: 'Plan good deeds', ar: 'خطط لأعمال صالحة', icon: '✨' },
      { en: 'Share goodness with others', ar: 'شارك الخير مع الآخرين', icon: '🤝' }
    ];
  };

  const prayerList = prayers.map(p => ({
    name: p.name,
    time: p.time,
    key: p.key
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
      <TimeAndPrayerDisplay prayers={prayerList} />

      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 text-9xl">🕌</div>
        <div className="absolute bottom-10 right-10 text-9xl">☪️</div>
        <div className="absolute top-1/2 left-1/4 text-7xl">✨</div>
        <div className="absolute top-1/4 right-1/4 text-7xl">🌙</div>
        <div className="absolute bottom-1/4 left-1/3 text-7xl">📿</div>
      </div>

      <div className="relative min-h-screen flex items-center justify-center p-3 md:p-6 pt-36 md:pt-40">
        <div className="w-full max-w-6xl px-4">
          {currentSlide === 'countdown' && (
            <div className="bg-white/95 backdrop-blur-lg rounded-3xl p-10 md:p-16 shadow-2xl border-t-4 border-amber-400 animate-fade-in w-full">
              <div className="text-center space-y-3">
                <div className="text-3xl md:text-4xl font-arabic text-amber-500 mb-2">
                  ﷽
                </div>

                <div className="text-6xl md:text-7xl mb-3 animate-pulse">
                  {getEventEmoji(event.name)}
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">
                  {event.name}
                </h1>

                <div className="text-3xl md:text-4xl font-arabic text-amber-600 mb-2" style={{ direction: 'rtl' }}>
                  {event.nameArabic}
                </div>

                <div className="text-lg md:text-xl text-gray-600 mb-6">
                  {event.description}
                </div>

                <div className="border-t-2 border-gray-200 my-6"></div>

                <div className="text-2xl md:text-3xl text-gray-600 mb-2">
                  Coming in
                </div>
                <div className="text-3xl md:text-4xl font-arabic text-teal-600 mb-6" style={{ direction: 'rtl' }}>
                  متبقي
                </div>

                {isLastDay ? (
                  <div className="flex items-center justify-center gap-6 md:gap-8">
                    <div className="text-center">
                      <div className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                        {hoursUntil}
                      </div>
                      <div className="text-lg md:text-xl text-gray-600 mt-2">
                        Hours
                      </div>
                      <div className="text-lg md:text-xl font-arabic text-teal-600 mt-1" style={{ direction: 'rtl' }}>
                        ساعات
                      </div>
                    </div>

                    <div className="text-4xl text-gray-400">:</div>

                    <div className="text-center">
                      <div className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                        {minutesUntil}
                      </div>
                      <div className="text-lg md:text-xl text-gray-600 mt-2">
                        Minutes
                      </div>
                      <div className="text-lg md:text-xl font-arabic text-cyan-600 mt-1" style={{ direction: 'rtl' }}>
                        دقائق
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="text-7xl md:text-9xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent leading-none">
                      {daysUntil}
                    </div>
                    <div className="text-3xl md:text-4xl text-gray-700 font-semibold">
                      {daysUntil === 1 ? 'Day' : 'Days'}
                    </div>
                    <div className="text-5xl md:text-6xl font-arabic text-teal-600 mt-3" style={{ direction: 'rtl' }}>
                      {daysUntil === 1 ? 'يوم' : 'أيام'}
                    </div>
                  </div>
                )}

                <div className="mt-10 text-xl md:text-2xl text-gray-500">
                  {new Date(event.date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          )}

          {currentSlide === 'hadith' && hadith && (
            <div className="text-center space-y-8 animate-fade-in">
              <div className="text-5xl md:text-7xl font-arabic text-amber-300 mb-4">
                ﷽
              </div>

              <div className="text-8xl mb-8">
                📖
              </div>

              <h2 className="text-5xl md:text-7xl font-bold text-white mb-4 drop-shadow-2xl">
                Hadith
              </h2>
              <div className="text-5xl md:text-7xl font-arabic text-amber-200" style={{ direction: 'rtl' }}>
                حديث شريف
              </div>

              <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl max-w-5xl mx-auto border-4 border-amber-400">
                <div className="text-7xl mb-8 text-amber-600">☪️</div>
                <p className="text-2xl md:text-4xl text-gray-900 leading-relaxed italic font-serif mb-8">
                  "{hadith.hadith_text}"
                </p>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="h-1 w-24 bg-gradient-to-r from-transparent via-amber-400 to-amber-400"></div>
                  <div className="text-3xl text-amber-600">۞</div>
                  <div className="h-1 w-24 bg-gradient-to-l from-transparent via-amber-400 to-amber-400"></div>
                </div>
                <p className="text-xl md:text-2xl text-amber-900 font-semibold">
                  {hadith.hadith_reference}
                </p>
              </div>
            </div>
          )}

          {currentSlide === 'preparation' && (
            <div className="bg-white/95 backdrop-blur-lg rounded-3xl p-10 md:p-16 shadow-2xl border-t-4 border-teal-400 animate-fade-in w-full">
              <div className="text-center space-y-4">
                <div className="text-3xl md:text-4xl font-arabic text-amber-500 mb-2">
                  ﷽
                </div>

                <div className="text-6xl md:text-7xl mb-3">
                  ✅
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">
                  Prepare Now
                </h2>
                <div className="text-3xl md:text-4xl font-arabic text-amber-600 mb-4" style={{ direction: 'rtl' }}>
                  استعد الآن
                </div>

                <div className="border-t-2 border-gray-200 my-6"></div>

                <div className="space-y-8 max-w-4xl mx-auto">
                  {getPreparationTips(event.name).map((tip, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border-4 border-teal-300"
                    >
                      <div className="text-6xl md:text-7xl mb-4">{tip.icon}</div>
                      <p className="text-2xl md:text-3xl text-gray-800 font-bold text-center leading-tight mb-4">
                        {tip.en}
                      </p>
                      <div className="border-t-2 border-teal-300 pt-4 mt-4">
                        <p className="text-3xl md:text-4xl font-arabic text-teal-700 text-center leading-relaxed" style={{ direction: 'rtl' }}>
                          {tip.ar}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-center gap-4 mt-8">
                  <div className="h-1 w-24 bg-gradient-to-r from-transparent via-teal-400 to-teal-400"></div>
                  <div className="text-4xl text-teal-600">۞</div>
                  <div className="h-1 w-24 bg-gradient-to-l from-transparent via-teal-400 to-teal-400"></div>
                </div>
                <div className="mt-6 text-xl md:text-2xl text-gray-700 font-bold">
                  May Allah accept from us and from you
                </div>
                <div className="text-2xl md:text-3xl font-arabic text-teal-600 mt-3" style={{ direction: 'rtl' }}>
                  تقبل الله منا ومنكم
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-3 mt-12">
            {(hadith ? ['countdown', 'hadith', 'preparation'] : ['countdown', 'preparation']).map((slide) => (
              <button
                key={slide}
                onClick={() => setCurrentSlide(slide as SlideType)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  currentSlide === slide
                    ? 'w-12 bg-white'
                    : 'w-3 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to ${slide} slide`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
